import {
    Directive,
    ElementRef,
    OnDestroy,
    OnInit
} from '@angular/core';
import { DimeProvider } from '@office-start/dime-service';
import FirstRunComponent from '@office-start/first-run-component';
import { RunType } from '@office-start/first-run-types';
import OfficeFluentProvider from '@office-start/office-fluent-provider';
import * as React from 'react';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { InstallConfigService } from '../install/install-config.service';
import { ObservableScope } from '../shared/models/observable-scope.enum';
import { bootstrapOfficeStartComponent } from '../shared/office-start/bootstrap-office-start-component';
import { OfficeStartBootstrapperService } from '../shared/office-start/office-start-bootstrapper.service';
import ReactRenderer from '../shared/react/react-renderer';
import { EventService } from '../shared/services/event.service';
import { ThemingService } from '../shared/services/theming.service';
import { AngularEvents } from '../shared/shared-context/angular-events';
import { DimeExperience, UpsellDataService } from '../upsell/upsell-data.service';
import { FirstRunReactStateService } from './first-run-react-state.service';

@Directive({ selector: 'ohp-first-run-react-content' })
export class FirstRunReactContentDirective extends ReactRenderer implements OnInit, OnDestroy {
    private dimeUpsellDialogOpenedSubscription: Subscription;
    private dimeUpsellDialogClosedSubscription: Subscription;
    private convertedToPaidSubscription: Subscription;

    public dimeActive: boolean;
    public userConvertedToPaid: boolean;

    private runType: RunType;
    private readonly dimeEnabled: boolean;
    public readonly upsellTrackingParam: string;

    constructor(
        elementRef: ElementRef,
        private readonly officeStartBootstrapperService: OfficeStartBootstrapperService,
        private readonly themingService: ThemingService,
        private readonly upsellDataService: UpsellDataService,
        private readonly eventService: EventService,
        private readonly firstRunReactStateService: FirstRunReactStateService,
        private readonly installConfigService: InstallConfigService
    ) {
        super(elementRef);
        bootstrapOfficeStartComponent(this.officeStartBootstrapperService, this.themingService);

        this.dimeEnabled = this.installConfigService.config.dimeEnabled;
        this.upsellTrackingParam = this.upsellDataService.getUpsellTrackingParam();

        this.firstRunReactStateService.getRunType().then(runType => {
            this.runType = runType;
        });
    }

    public ngOnInit() {
        if (this.dimeEnabled) {
            this.prepareDimeUpsellExperience();
        }

        this.dimeActive = false;
        this.userConvertedToPaid = false;

        super.ngOnInit();
    }

    public ngOnDestroy() {
        this.unsubscribeFromDimeEvents();

        super.ngOnDestroy();
    }

    public async getReactElement () {
        return (
            <OfficeFluentProvider>
                <DimeProvider>
                <FirstRunComponent
                    dimeActive={this.dimeActive}
                    dimeEnabled={this.dimeEnabled}
                    OEMCode={this.upsellTrackingParam}
                    showDime={this.showDime}
                    userConvertedToPaid={this.userConvertedToPaid}
                    runType={this.runType}
                    />
                </DimeProvider>
            </OfficeFluentProvider>
        );
    }

    private readonly showDime = (targetUrl: string) => {
        if (this.dimeEnabled) {
            this.upsellDataService.launchDime(DimeExperience.Fre, targetUrl);
        }
    }

    /**
     * Warms up Dime service and sets up listeners for events emitted by Dime.
     */
    private prepareDimeUpsellExperience() {
        this.upsellDataService.warmupDime(DimeExperience.Fre);
        this.subscribeToDimeEvents();
    }

    private subscribeToDimeEvents() {
        this.dimeUpsellDialogOpenedSubscription = this.eventService.events.withScope(ObservableScope.OnlyFuture)
            .pipe(
                filter(event => event.name === AngularEvents.dimeUpsellDialogOpened)
            )
            .subscribe(() => {
                this.dimeActive = true;
                this.render();
            });

        this.dimeUpsellDialogClosedSubscription = this.eventService.events.withScope(ObservableScope.OnlyFuture)
            .pipe(
                filter(event => event.name === AngularEvents.dimeUpsellDialogClosed)
            )
            .subscribe(() => {
                this.dimeActive = false;
                this.render();
            });

        this.convertedToPaidSubscription = this.eventService.events.withScope(ObservableScope.OnlyFuture)
            .pipe(
                filter(event => event.name === AngularEvents.convertedToPaid)
            )
            .subscribe(() => {
                this.userConvertedToPaid = true;

                // TODO: implement forceRefreshAndGetLicenseStatus() from first-run-data.service.ts if needed.

                this.render();
            });
    }

    private unsubscribeFromDimeEvents() {
        if (this.dimeUpsellDialogOpenedSubscription) {
            this.dimeUpsellDialogOpenedSubscription.unsubscribe();
        }

        if (this.dimeUpsellDialogClosedSubscription) {
            this.dimeUpsellDialogClosedSubscription.unsubscribe();
        }

        if (this.convertedToPaidSubscription) {
            this.convertedToPaidSubscription.unsubscribe();
        }
    }
}
